import { RecordFolderAssignment } from '@provision/entity-types';
import { PageRoots, portalPaths } from '@provision/portal-routes';
import { ReactNode, useMemo } from 'react';
import { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { mapAssignmentToNavItem } from 'src/components/Navbar/utils';
import {
  RecordFolder,
  useCurrentUserProfile,
  useGroupRules,
  UserGroupRules,
  useViewRecordFolders,
} from 'src/graphql';
import { CustomViewNavItem } from 'src/graphql/entities/custom-view';
import { RecordFolderVisibility } from 'src/graphql/entities/record-folder';
import { useDeleteRecordFolderAssignment } from 'src/graphql/entities/record-folder-assignment';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { type NavigationDestination } from 'src/utils/navigation/navigation.types';
import { useNavigationDestinations } from 'src/utils/navigation/use-navigation-destinations';
import { useNavigateRoute } from 'src/utils/routing';
import { trimDuplicatedSlashes } from 'src/utils/routing/use-navigate-route';

export interface NavigationDataItem extends NavigationDestination {
  menuItems?: EllipsesMenuOption[];
  rawAssignmentItem?: RecordFolderAssignment;
}

export interface NavigationGroup {
  id: string;
  title: string;
  icon?: string;
  items: NavigationDataItem[];
  onClick?: (item: NavigationDataItem) => void;
  onDelete?: (item: NavigationDataItem) => void;
  onReorder?: (items: CustomViewNavItem[]) => void;
  onShowHidden?: () => void;
  shouldShowHiddenItems?: boolean;
  rightAdornment?: ReactNode;
  enableFolderCreation?: boolean;
  folder?: RecordFolder;
  hiddenGroups?: NavigationGroup[];
  isHidden?: boolean;
  allowEmpty?: boolean;
}

const EMPTY_NAVIGATION_GROUPS: NavigationGroup[] = [];

interface NavigationItems {
  navigationGroups: NavigationGroup[];
  settingsNavigationGroups: NavigationGroup[];
  landingRoute: string | null;
  loading: boolean;
}

export function useNavigationItems(): NavigationItems {
  const { user } = useCurrentUserProfile();
  const { partnerPrefix, loading: urlIsLoading } = useNavigateRoute();
  const { checkValidity } = useGroupRules();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { shouldShowHiddenFolders, setShowHiddenFolders } = useNavigationStore();

  const {
    workspaceItems,
    favoriteViews,
    settingsGroups,
    customViews,
    reorderFavoriteViews,
    filterByAccess,
    isReady,
    loading,
  } = useNavigationDestinations();
  const { recordFolders, isEnabled, reorderAssignments } = useViewRecordFolders();
  const { deleteRecordFolderAssignment } = useDeleteRecordFolderAssignment();

  const navigationGroups = useMemo<NavigationGroup[] | null>(() => {
    if (!isReady) {
      return null;
    }

    const visibleFolders = recordFolders?.filter(
      (folder) =>
        (folder.assignments?.length || folder.userId === user?.id) &&
        (shouldShowHiddenFolders || folder.visibility === RecordFolderVisibility.Shown),
    );

    const groups: NavigationGroup[] = [
      {
        id: 'favorites',
        title: 'Favorites',
        items: favoriteViews,
        onReorder: reorderFavoriteViews,
        shouldShowHiddenItems:
          recordFolders?.length && !visibleFolders?.length
            ? shouldShowHiddenFolders
            : undefined,
        onShowHidden: () => {
          setShowHiddenFolders(!shouldShowHiddenFolders);
        },
      },
    ];

    if (recordFolders?.length) {
      visibleFolders.forEach((folder, index) => {
        const isOwn = folder.userId === user?.id;

        const visibleItems = folder.assignments
          ?.map((assignment) =>
            mapAssignmentToNavItem({
              assignment,
              customViews,
              folder,
              selectedPartnerId,
              deleteAssignment:
                (folder.isPrivate && isOwn) ||
                checkValidity([UserGroupRules.AccessToManagePublicFolders])
                  ? deleteRecordFolderAssignment
                  : undefined,
            }),
          )
          .filter((item) => Boolean(item.path));

        if (visibleItems?.length || isOwn) {
          groups.push({
            id: folder.id,
            icon: folder?.isPrivate && isOwn ? 'lock' : '',
            title: folder.name,
            isHidden: folder.visibility === RecordFolderVisibility.Hidden,
            onReorder: reorderAssignments,
            shouldShowHiddenItems:
              index === visibleFolders.length - 1 ? shouldShowHiddenFolders : undefined,
            onShowHidden: () => {
              setShowHiddenFolders(!shouldShowHiddenFolders);
            },
            items: filterByAccess(visibleItems ?? []),
            folder,
            enableFolderCreation: index === visibleFolders.length - 1 && isEnabled,
            allowEmpty: true,
          });
        }
      });
    }

    groups.push({
      id: 'workspace',
      title: 'Workspace',
      items: workspaceItems,
      enableFolderCreation: isEnabled,
    });

    return groups.filter((group) => group.allowEmpty || group.items?.length);
  }, [
    isReady,
    workspaceItems,
    favoriteViews,
    customViews,
    reorderFavoriteViews,
    recordFolders,
    reorderAssignments,
    shouldShowHiddenFolders,
    selectedPartnerId,
    deleteRecordFolderAssignment,
    setShowHiddenFolders,
    checkValidity,
    filterByAccess,
    user?.id,
    isEnabled,
  ]);

  const allNavigationItems = useMemo(() => {
    if (!navigationGroups) {
      return null;
    }
    return [...navigationGroups, ...settingsGroups].flatMap((group) => group.items);
  }, [navigationGroups, settingsGroups]);

  const landingRoute = useMemo(() => {
    if (!allNavigationItems || urlIsLoading) {
      return null;
    }

    /**
     * Application starting page is determined by user's access rights and multitenancy mode.
     * 'All Conversations' is a default route, but if they have no Conversations access,
     * then we redirect to CRM page.
     * If no menu items are available, because of restrictions imposed by conditions above,
     * we redirect the user to 404 page.
     */

    const conversationsItem = allNavigationItems.find(
      (item) =>
        item.path ===
        portalPaths.entity.all({
          entityName: 'conversations',
        }),
    );

    if (conversationsItem) {
      return conversationsItem.path;
    }

    const crmOrganizations = allNavigationItems.find(
      (item) => item.path === portalPaths.crm.organizations(),
    );

    if (crmOrganizations) {
      return crmOrganizations.path;
    }

    if (allNavigationItems.length) {
      return allNavigationItems[0].path;
    }

    return PageRoots.notFound;
  }, [allNavigationItems, urlIsLoading]);

  return {
    navigationGroups: navigationGroups || EMPTY_NAVIGATION_GROUPS,
    settingsNavigationGroups: settingsGroups,
    landingRoute: landingRoute
      ? trimDuplicatedSlashes(`${partnerPrefix}/${landingRoute}`)
      : null,
    loading,
  };
}
